import { createColumnHelper, type SortingState } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { MakeupBooking } from '~entities/makeup/api/book-makeup';
import { useGetMakeupsByClassDate } from '~entities/makeup/api/get-makeups-by-class-date';
import { useSendMakeupConfirmationMutation } from '~entities/makeup/api/send-makeup-confirmation';
import {
  type MakeupStatusUpdate,
  useUpdateMakeupStatusMutation,
} from '~entities/makeup/api/update-makeup-status';

import { compareAttendanceStudentsByName } from '~features/classes/lib/attendance-student';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { getErrorMessage } from '~shared/utils/error-handler';

function getStudentNameParts(booking: MakeupBooking): {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
} {
  const student = booking.studentId;
  if (typeof student === 'string') return { name: student };
  const composed = [student.firstName, student.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return {
    firstName: student.firstName,
    lastName: student.lastName,
    name: student.name?.trim() ? student.name : composed || student._id,
  };
}

function getStudentName(booking: MakeupBooking): string {
  const parts = getStudentNameParts(booking);
  const composed = [parts.firstName, parts.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (parts.name?.trim()) return parts.name;
  return composed;
}

function getSourceClassTitle(booking: MakeupBooking): string {
  const source = booking.sourceEnrollmentId;
  if (!source) return 'Unknown class';
  if (typeof source === 'string') return 'Class';
  if (!source.title) return 'Class';
  return source.title;
}

function formatAbsenceDate(value: string): string {
  try {
    return format(parseISO(String(value).slice(0, 10)), 'MMM d, yyyy');
  } catch {
    return String(value).slice(0, 10);
  }
}

function MakeupStatusCell({ status }: { status: MakeupBooking['status'] }) {
  const badgeSize =
    'inline-flex min-w-[5.75rem] shrink-0 justify-center whitespace-nowrap';

  if (status === 'completed') {
    return (
      <Badge
        className={`${badgeSize} border-transparent bg-emerald-100 text-emerald-800 hover:bg-emerald-100`}
      >
        Made up
      </Badge>
    );
  }

  if (status === 'no_show') {
    return (
      <Badge
        className={`${badgeSize} border-transparent bg-slate-100 text-slate-700 hover:bg-slate-100`}
      >
        No Show
      </Badge>
    );
  }

  if (status === 'cancelled') {
    return (
      <Badge
        variant='outline'
        className={`${badgeSize} border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/10`}
      >
        Cancelled
      </Badge>
    );
  }

  return (
    <Badge variant='secondary' className={`${badgeSize} capitalize`}>
      {String(status).replace('_', ' ')}
    </Badge>
  );
}

/** ENR-3438: collapse makeup row actions into a mobile-friendly dropdown. */
function MakeupActionsMenu({
  bookingId,
  status,
  isUpdating,
  isSending,
  isCompleting,
  onStatus,
  onResend,
}: {
  bookingId: string;
  status: MakeupBooking['status'];
  isUpdating: boolean;
  isSending: boolean;
  isCompleting: boolean;
  onStatus: (makeupId: string, status: MakeupStatusUpdate) => void;
  onResend: (makeupId: string) => void;
}) {
  const busy = isUpdating || isSending;
  const showScheduledActions = status === 'scheduled';
  const showRequestedActions = status === 'requested';

  if (!showScheduledActions && !showRequestedActions) {
    return <span className='text-xs text-muted-foreground'>—</span>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='h-8 gap-1.5 px-2.5'
          disabled={busy}
        >
          {isCompleting || isSending ? (
            <Loader2 className='size-3.5 animate-spin' />
          ) : (
            <MoreHorizontal className='size-4' />
          )}
          Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {showScheduledActions ? (
          <>
            <DropdownMenuItem
              disabled={busy}
              onClick={() => {
                onStatus(bookingId, 'completed');
              }}
            >
              Complete
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={busy}
              onClick={() => {
                onStatus(bookingId, 'no_show');
              }}
            >
              No Show
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={busy}
              className='text-destructive focus:text-destructive'
              onClick={() => {
                onStatus(bookingId, 'cancelled');
              }}
            >
              Cancel
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={busy}
              onClick={() => {
                onResend(bookingId);
              }}
            >
              Resend email
            </DropdownMenuItem>
          </>
        ) : null}
        {showRequestedActions ? (
          <DropdownMenuItem
            disabled={busy}
            className='text-destructive focus:text-destructive'
            onClick={() => {
              onStatus(bookingId, 'cancelled');
            }}
          >
            Cancel
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const columnHelper = createColumnHelper<MakeupBooking>();

interface MakeupStudentTableProps {
  classId: string;
  formattedDate: string;
  enabled?: boolean;
}

export function MakeupStudentTable({
  classId,
  formattedDate,
  enabled = true,
}: MakeupStudentTableProps) {
  const { data = [], isLoading } = useGetMakeupsByClassDate(
    { enrollmentId: classId, date: formattedDate },
    enabled,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'student', desc: false },
  ]);
  const updateStatusMutation = useUpdateMakeupStatusMutation();
  const sendConfirmationMutation = useSendMakeupConfirmationMutation();

  const tableData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return data;
    return data.filter((booking) => {
      const parts = getStudentNameParts(booking);
      const haystack = [parts.name, parts.firstName, parts.lastName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [data, searchQuery]);

  const sortedData = useMemo(() => {
    const rows = [...tableData];
    const descending = sorting[0]?.desc ?? false;
    rows.sort((a, b) => {
      const cmp = compareAttendanceStudentsByName(
        getStudentNameParts(a),
        getStudentNameParts(b),
        'lastName',
      );
      return descending ? -cmp : cmp;
    });
    return rows;
  }, [sorting, tableData]);

  const handleStatus = useCallback(
    async (makeupId: string, status: MakeupStatusUpdate) => {
      try {
        await updateStatusMutation.mutateAsync({ makeupId, status });
        toast.success(
          status === 'completed'
            ? 'Makeup marked completed.'
            : status === 'no_show'
              ? 'Makeup marked as No Show.'
              : 'Makeup cancelled.',
        );
      } catch (error) {
        const fallback = 'Failed to update makeup status.';
        try {
          const message = getErrorMessage(error);
          toast.error(
            typeof message === 'string' && message.trim()
              ? message.trim()
              : fallback,
          );
        } catch {
          toast.error(fallback);
        }
      }
    },
    [updateStatusMutation],
  );

  const handleResendConfirmation = useCallback(
    async (makeupId: string) => {
      try {
        await sendConfirmationMutation.mutateAsync({ makeupId });
        toast.success('Confirmation email sent.');
      } catch (error) {
        const fallback = 'Failed to send confirmation email.';
        try {
          const message = getErrorMessage(error);
          toast.error(
            typeof message === 'string' && message.trim()
              ? message.trim()
              : fallback,
          );
        } catch {
          toast.error(fallback);
        }
      }
    },
    [sendConfirmationMutation],
  );

  const cols = useMemo(
    () => [
      columnHelper.accessor((row) => getStudentName(row), {
        id: 'student',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Student'
            className='pl-4 text-black'
          />
        ),
        sortingFn: (rowA, rowB) =>
          compareAttendanceStudentsByName(
            getStudentNameParts(rowA.original),
            getStudentNameParts(rowB.original),
            'lastName',
          ),
        cell: ({ row }) => (
          <span className='font-medium'>{getStudentName(row.original)}</span>
        ),
      }),
      columnHelper.accessor((row) => getSourceClassTitle(row), {
        id: 'source',
        header: 'From absence',
        cell: ({ row }) => (
          <span className='text-sm text-muted-foreground'>
            {getSourceClassTitle(row.original)} ·{' '}
            {formatAbsenceDate(row.original.sourceAttendanceDate)}
          </span>
        ),
      }),
      columnHelper.accessor('bookingMode', {
        header: 'Booked by',
        cell: ({ getValue }) => (
          <span className='text-sm capitalize text-muted-foreground'>
            {getValue() === 'parent_request' ? 'Parent' : 'Staff'}
          </span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ getValue }) => <MakeupStatusCell status={getValue()} />,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const booking = row.original;
          const isUpdating =
            updateStatusMutation.isPending &&
            updateStatusMutation.variables.makeupId === booking._id;
          const isSending =
            sendConfirmationMutation.isPending &&
            sendConfirmationMutation.variables.makeupId === booking._id;

          return (
            <MakeupActionsMenu
              bookingId={booking._id}
              status={booking.status}
              isUpdating={isUpdating}
              isSending={isSending}
              isCompleting={
                isUpdating &&
                updateStatusMutation.variables.status === 'completed'
              }
              onStatus={handleStatus}
              onResend={handleResendConfirmation}
            />
          );
        },
      }),
    ],
    [
      updateStatusMutation.isPending,
      updateStatusMutation.variables,
      sendConfirmationMutation.isPending,
      sendConfirmationMutation.variables,
      handleStatus,
      handleResendConfirmation,
    ],
  );

  if (!enabled) {
    return null;
  }

  return (
    <div>
      <h2 className='mb-4 text-xl font-semibold'>Makeup Students</h2>

      {isLoading ? (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='size-4 animate-spin' />
          Loading makeups…
        </div>
      ) : data.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          No makeup students for this class date.
        </p>
      ) : (
        <>
          <div className='space-y-3 md:hidden'>
            <EnrollioTableSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder='Search makeup students...'
            />
            {sortedData.map((booking) => {
              const isUpdating =
                updateStatusMutation.isPending &&
                updateStatusMutation.variables.makeupId === booking._id;
              const isSending =
                sendConfirmationMutation.isPending &&
                sendConfirmationMutation.variables.makeupId === booking._id;

              return (
                <div key={booking._id} className='rounded-md border p-3'>
                  <div className='mb-1 flex items-start justify-between gap-2'>
                    <div className='min-w-0 flex-1 font-medium'>
                      {getStudentName(booking)}
                    </div>
                    <MakeupActionsMenu
                      bookingId={booking._id}
                      status={booking.status}
                      isUpdating={isUpdating}
                      isSending={isSending}
                      isCompleting={
                        isUpdating &&
                        updateStatusMutation.variables.status === 'completed'
                      }
                      onStatus={handleStatus}
                      onResend={handleResendConfirmation}
                    />
                  </div>
                  <div className='mb-2 text-xs text-muted-foreground'>
                    From {getSourceClassTitle(booking)} ·{' '}
                    {formatAbsenceDate(booking.sourceAttendanceDate)} ·{' '}
                    {booking.bookingMode === 'parent_request'
                      ? 'Parent'
                      : 'Staff'}
                  </div>
                  <MakeupStatusCell status={booking.status} />
                </div>
              );
            })}
            {sortedData.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No makeup students match your search.
              </p>
            ) : null}
          </div>

          <div className='hidden md:block'>
            <EnrollioTable
              ariaLabel='Makeup students'
              totalLabel='Makeup Students'
              filterHeader={
                <EnrollioTableSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder='Search makeup students...'
                />
              }
              columns={cols}
              data={tableData}
              getRowId={(row) => row._id}
              sorting={sorting}
              onSortingChange={setSorting}
              mobile={false}
            />
          </div>
        </>
      )}
    </div>
  );
}
